import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatINR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, PackageSearch, Download, Check, MessageSquare, Boxes, KeyRound } from "lucide-react";
import OrderTimeline from "@/components/OrderTimeline";
import STLViewer from "@/components/STLViewer";
import usePreloadUrls from "@/hooks/usePreloadUrls";
import SectionAccents from "@/components/SectionAccents";

const extractExt = (url) => {
    const clean = (url || "").split("?")[0];
    const m = clean.match(/\.[a-zA-Z0-9]{2,5}$/);
    return m ? m[0] : ".jpg";
};

const uploadUrl = (p) => (p && p.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${p}`);
const isGlb = (p) => (p || "").toLowerCase().endsWith(".glb");
const isStl = (p) => (p || "").toLowerCase().endsWith(".stl");
const is3mf = (p) => (p || "").toLowerCase().endsWith(".3mf");

const downloadPhoto = async (src, filename) => {
    try {
        const resp = await fetch(src, { credentials: "omit" });
        if (!resp.ok) throw new Error("bad response");
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
        window.open(src, "_blank");
    }
};

const OrderTracking = () => {
    const [params] = useSearchParams();
    const [orderNumber, setOrderNumber] = useState(params.get("number") || "");
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(null);

    const approve = async (idx) => {
        setBusy(`${idx}`);
        try {
            await api.post(`/orders/track/${order.order_number}/items/${idx}/approve`);
            toast.success("Preview approved — we'll begin production!");
            await search(order.order_number);
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setBusy(null); }
    };

    const requestChanges = async (idx) => {
        const note = window.prompt("Describe the changes you'd like (pose, colour, expression…):");
        if (!note) return;
        setBusy(`${idx}-r`);
        try {
            await api.post(`/orders/track/${order.order_number}/items/${idx}/revise`, { note });
            toast.success("Change request sent — our team will get on it!");
            await search(order.order_number);
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setBusy(null); }
    };

    const search = async (num) => {
        const value = num || orderNumber;
        if (!value) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/orders/track/${value.trim().toUpperCase()}`);
            setOrder(data);
        } catch {
            setOrder(null);
            toast.error("Order not found");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (params.get("number")) search(params.get("number"));
        // eslint-disable-next-line
    }, []);

    // Pre-warm the browser cache for STL/GLB files as soon as tracking data loads.
    const preloadHrefs = React.useMemo(() => {
        const urls = [];
        for (const it of (order?.items || [])) {
            for (const p of (it.production_files || [])) {
                if (isStl(p) || isGlb(p) || is3mf(p)) urls.push(uploadUrl(p));
            }
        }
        return urls;
    }, [order]);
    usePreloadUrls(preloadHrefs);

    const addr = order?.address || {};

    return (
        <div className="relative max-w-4xl mx-auto px-6 py-12 overflow-hidden" data-testid="track-page">
            <SectionAccents variant="track" />
            <div className="text-xs uppercase tracking-widest text-brand-gold font-semibold">Live tracking</div>
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-1">Track your order</h1>
            <p className="text-muted-foreground mt-2">Enter your order number (e.g. CHR-260101-A1B2C3) to see live progress.</p>

            <div className="mt-6 flex gap-2">
                <Input value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} placeholder="CHR-XXXXXX-XXXXXX" data-testid="track-input" className="rounded-full h-12 bg-white/60 border-primary/20" />
                <Button onClick={() => search()} disabled={loading} data-testid="track-btn" className="rounded-full h-12 px-6 bg-primary hover:bg-primary/90">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackageSearch className="w-4 h-4" />} Track
                </Button>
            </div>

            {order && (
                <div className="mt-8 rounded-3xl glass p-6 space-y-6" data-testid="track-result">
                    {params.get("welcome") === "1" && order.address?.email && (
                        <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-900 flex items-start gap-3" data-testid="track-welcome-login">
                            <KeyRound className="w-4 h-4 mt-0.5 shrink-0" />
                            <div>
                                <b>Your account is ready.</b> We've emailed a temporary password to <b>{order.address.email}</b> so you can sign in anytime to see your orders and 3D previews.
                                {" "}<a href="/login" className="underline font-semibold" data-testid="track-welcome-login-link">Sign in</a>
                            </div>
                        </div>
                    )}
                    {/* Header */}
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                            <h2 className="font-display text-2xl font-bold tracking-tight" data-testid="track-order-number">Order {order.order_number}</h2>
                            <div className="text-xs text-muted-foreground mt-0.5">Placed {new Date(order.created_at).toLocaleString()}</div>
                        </div>
                        <span className={`text-[11px] px-2.5 py-0.5 rounded-full font-semibold uppercase tracking-widest ${order.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : order.payment_status === "cod_pending" ? "bg-blue-100 text-blue-700" : order.payment_status === "cod_awaiting_confirmation" ? "bg-amber-100 text-amber-800" : "bg-yellow-100 text-yellow-700"}`} data-testid="track-payment-badge">
                            {order.payment_status === "cod_pending" ? "COD — Pay on delivery" : order.payment_status === "cod_awaiting_confirmation" ? "COD — Check your email to confirm" : order.payment_status}
                        </span>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        {/* Customer */}
                        <div data-testid="track-customer">
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Customer</div>
                            <div className="text-sm font-semibold">{order.customer?.full_name || addr.full_name}</div>
                            <div className="text-xs text-muted-foreground mt-0.5">{order.customer?.phone || addr.phone}{order.customer?.email ? ` · ${order.customer.email}` : addr.email ? ` · ${addr.email}` : ""}</div>
                            <div className="text-xs text-muted-foreground mt-2 leading-relaxed">
                                {[addr.line1, addr.line2, addr.city, addr.state, addr.pincode, addr.country].filter(Boolean).join(", ")}
                            </div>
                        </div>

                        {/* Items */}
                        <div data-testid="track-items">
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Items</div>
                            <div className="space-y-3">
                                {(order.items || []).map((it, idx) => {
                                    const photos = it.reference_photos || [];
                                    return (
                                        <div key={idx} className="rounded-2xl bg-white/60 p-3" data-testid={`track-item-${idx}`}>
                                            <div className="font-semibold text-sm">{it.product_name}</div>
                                            <div className="text-xs text-muted-foreground">{it.size_label} · Qty {it.quantity} · {formatINR(it.unit_price_inr || 0)}</div>
                                            {it.selected_options && Object.keys(it.selected_options).length > 0 && (
                                                <div className="mt-1 flex flex-wrap gap-1">
                                                    {Object.entries(it.selected_options).map(([k, v]) => (
                                                        <span key={k} className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary">{k}: {v}</span>
                                                    ))}
                                                </div>
                                            )}
                                            {photos.length > 0 && (
                                                <div className="mt-3">
                                                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Customer photos ({photos.length})</div>
                                                    <div className="flex gap-2 flex-wrap">
                                                        {photos.map((p, i) => {
                                                            const src = p.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}/api/uploads/${p}`;
                                                            const filename = `${order.order_number}-item-${idx + 1}-photo-${i + 1}${extractExt(src)}`;
                                                            return (
                                                                <div key={i} className="relative group w-16 h-16 rounded-xl overflow-hidden border" data-testid={`track-ref-${idx}-${i}`}>
                                                                    <a href={src} target="_blank" rel="noreferrer" className="block w-full h-full">
                                                                        <img src={src} alt={`Reference ${i + 1}`} className="w-full h-full object-cover" />
                                                                    </a>
                                                                    <button
                                                                        onClick={() => downloadPhoto(src, filename)}
                                                                        data-testid={`track-download-${idx}-${i}`}
                                                                        title="Download image"
                                                                        className="absolute bottom-1 right-1 w-5 h-5 rounded-full bg-white/95 hover:bg-primary hover:text-white text-primary flex items-center justify-center shadow"
                                                                    >
                                                                        <Download className="w-3 h-3" />
                                                                    </button>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                            {(() => {
                                                const files = it.production_files || [];
                                                if (!files.length) return null;
                                                const mf = [...files].reverse().find(is3mf);
                                                const glb = !mf ? [...files].reverse().find(isGlb) : null;
                                                const stl = !mf && !glb ? [...files].reverse().find(isStl) : null;
                                                const meshFile = mf || glb || stl;
                                                return (
                                                    <div className="mt-4 pt-3 border-t border-border/60" data-testid={`track-studio-${idx}`}>
                                                        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                                                            <div className="text-[10px] uppercase tracking-widest text-brand-gold font-semibold inline-flex items-center gap-1">
                                                                <Boxes className="w-3 h-3" /> Studio preview
                                                            </div>
                                                            {it.approval_status === "approved" ? (
                                                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 inline-flex items-center gap-1"><Check className="w-3 h-3" /> Approved</span>
                                                            ) : it.approval_status === "revision_requested" ? (
                                                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Changes requested</span>
                                                            ) : (
                                                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-100 text-sky-800">Awaiting your approval</span>
                                                            )}
                                                        </div>
                                                        {meshFile ? (
                                                            <div className="rounded-xl overflow-hidden bg-gradient-to-br from-brand-cream to-brand-lavender relative" style={{ height: 280 }}>
                                                                <STLViewer src={uploadUrl(meshFile)} height={280} testId={`track-viewer-${idx}`} />
                                                                {mf && <div className="absolute top-2 left-2 text-[10px] px-2 py-1 rounded-full bg-white/70 text-brand-gold font-semibold pointer-events-none" data-testid={`track-3mf-badge-${idx}`}>Colour preview (3MF)</div>}
                                                                <div className="absolute bottom-2 right-2 text-[10px] px-2 py-1 rounded-full bg-black/40 text-white pointer-events-none">Drag · Scroll to zoom</div>
                                                            </div>
                                                        ) : null}
                                                        {it.approval_status === "approved" && it.approved_at && (
                                                            <div className="mt-2 rounded-xl bg-emerald-50 border border-emerald-200 p-2 text-[11px] text-emerald-800 inline-flex items-center gap-1" data-testid={`track-approved-${idx}`}><Check className="w-3 h-3" /> You approved this preview on {new Date(it.approved_at).toLocaleDateString()}. We're now producing your piece.</div>
                                                        )}
                                                        {it.approval_status === "revision_requested" && it.revision_note && (
                                                            <div className="mt-2 rounded-xl bg-amber-50 border border-amber-200 p-2 text-[11px] text-amber-900" data-testid={`track-revision-note-${idx}`}>
                                                                <b>Your note:</b> "{it.revision_note}" — our team will upload a revised preview shortly.
                                                            </div>
                                                        )}
                                                        {it.approval_status !== "approved" && it.approval_status !== "revision_requested" && (
                                                            <div className="mt-3 space-y-2" data-testid={`track-approval-${idx}`}>
                                                                <div className="text-[11px] text-muted-foreground">Happy with this preview? Approve to begin production — or request tweaks.</div>
                                                                <div className="flex flex-wrap gap-2">
                                                                    <button
                                                                        onClick={() => approve(idx)}
                                                                        disabled={busy === `${idx}`}
                                                                        data-testid={`track-approve-${idx}`}
                                                                        className="text-xs px-4 h-9 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1 disabled:opacity-50"
                                                                    >
                                                                        {busy === `${idx}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Approve preview
                                                                    </button>
                                                                    <button
                                                                        onClick={() => requestChanges(idx)}
                                                                        disabled={busy === `${idx}-r`}
                                                                        data-testid={`track-request-changes-${idx}`}
                                                                        className="text-xs px-4 h-9 rounded-full border border-primary/40 text-primary hover:bg-primary/5 inline-flex items-center gap-1 disabled:opacity-50"
                                                                    >
                                                                        <MessageSquare className="w-3 h-3" /> Request changes
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Payment summary */}
                    <div className="pt-4 border-t border-border" data-testid="track-payment">
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Payment</div>
                        <div className="text-sm space-y-1">
                            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{formatINR(order.subtotal_inr || 0)}</span></div>
                            {(order.discount_inr || 0) > 0 && <div className="flex justify-between text-emerald-700"><span>Discount</span><span>-{formatINR(order.discount_inr)}</span></div>}
                            <div className="flex justify-between"><span className="text-muted-foreground">GST</span><span>{formatINR(order.gst_inr || 0)}</span></div>
                            <div className="flex justify-between"><span className="text-muted-foreground">Shipping</span><span>{(order.shipping_inr || 0) === 0 ? "Free" : formatINR(order.shipping_inr)}</span></div>
                            <div className="flex justify-between font-display text-base font-bold pt-2 border-t border-border/60 mt-2"><span>Total</span><span data-testid="track-total">{formatINR(order.total_inr || 0)}</span></div>
                        </div>
                    </div>

                    {/* Progress */}
                    <div className="pt-4 border-t border-border">
                        <div className="font-display text-lg font-semibold mb-3">Progress</div>
                        <OrderTimeline currentStage={order.status} history={order.timeline || []} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrderTracking;
